import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { TanTable } from './TanTable';
import yardService from '../services/yard.service';
import { YardEventItem } from '../types/yard';

interface YardEventsPanelProps {
  pronumber: number | null;
  /** Defer fetching until the panel is actually shown. */
  active?: boolean;
  /** Bump this number to trigger a refetch from the parent. */
  refreshNonce?: number;
}

const formatDateTime = (isoString: string | null | undefined): string => {
  if (!isoString) return '';
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
};

const eventTypeLabel = (t: string): string => {
  if (t === 'I') return 'In';
  if (t === 'O') return 'Out';
  if (t === 'R') return 'Reset';
  return t;
};

const eventTypeBadgeClass = (t: string): string => {
  if (t === 'I') return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
  if (t === 'O') return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
  if (t === 'R') return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
  return 'bg-gray-100 text-label dark:bg-gray-700';
};

export const YardEventsPanel: React.FC<YardEventsPanelProps> = ({ pronumber, active = true, refreshNonce = 0 }) => {
  const [events, setEvents] = useState<YardEventItem[]>([]);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    if (!pronumber) return;
    setLoading(true);
    try {
      const data = await yardService.getEvents({ cmspronumber: pronumber, limit: 500 });
      setEvents(data);
    } catch (err) {
      console.error('Failed to fetch yard events:', err);
    } finally {
      setLoading(false);
    }
  }, [pronumber]);

  useEffect(() => {
    if (active && pronumber) {
      fetch();
    }
  }, [active, pronumber, fetch, refreshNonce]);

  const columns = useMemo<ColumnDef<YardEventItem>[]>(() => [
    {
      accessorKey: 'eventdatetime',
      header: 'Date/Time',
      size: 170,
      cell: (info) => formatDateTime(info.getValue() as string),
    },
    {
      accessorKey: 'eventtype',
      header: 'Type',
      size: 90,
      cell: (info) => {
        const v = info.getValue() as string;
        return (
          <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${eventTypeBadgeClass(v)}`}>
            {eventTypeLabel(v)}
          </span>
        );
      },
    },
    {
      accessorKey: 'yardloc',
      header: 'Yard',
      size: 200,
      cell: (info) => (info.getValue() as string) || '-',
    },
    {
      accessorKey: 'equipno',
      header: 'Equipment #',
      size: 140,
      cell: (info) => (info.getValue() as string) || '-',
    },
    {
      accessorKey: 'chassisno',
      header: 'Chassis #',
      size: 120,
      cell: (info) => (info.getValue() as string) || '-',
    },
    {
      accessorKey: 'loadempty',
      header: 'Load',
      size: 80,
      cell: (info) => {
        const v = info.getValue() as string;
        return v === 'L' ? 'Loaded' : v === 'E' ? 'Empty' : '-';
      },
    },
    {
      accessorKey: 'companyname',
      header: 'Company',
      size: 200,
      cell: (info) => (info.getValue() as string) || '-',
    },
    {
      accessorKey: 'createdby',
      header: 'User',
      size: 140,
      cell: (info) => (info.getValue() as string) || '-',
    },
    {
      accessorKey: 'comments',
      header: 'Comments',
      size: 220,
      cell: (info) => (info.getValue() as string) || '-',
    },
  ], []);

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 min-h-[200px]">
        <TanTable
          data={events}
          columns={columns}
          loading={loading}
          cardTitle="Yard Events"
          searchPlaceholder="Search events..."
          fillContainer
          enableSearch
          enableReset
          enableShowHideCols
          enableColumnFilters
          enableZebraStriping={false}
          defaultSorting={[{ id: 'eventdatetime', desc: true }]}
        />
      </div>
    </div>
  );
};

export default YardEventsPanel;
